import { useState, useEffect } from 'react';
import { Layout } from '@/components/common/Layout/Layout';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Plus, Edit, Trash2, Lock, UserX } from 'lucide-react';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { User } from '@/lib/store';
import { useStore } from '@/lib/store';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/hooks/useAuth';
import { Switch } from '@/components/ui/switch';

// Type for the user form
type UserFormData = Omit<User, 'id'> & {
  password?: string;
  confirm_password?: string;
};

export function Users() {
  const { users, fetchUsers } = useStore();
  const { user: currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const { toast } = useToast();

  // Initial form data for creating a new user
  const initialFormData: UserFormData = {
    email: '',
    first_name: '',
    last_name: '',
    title: '',
    language: 'en',
    default_rate: 0,
    yearly_target: 1300,
    form_of_salary: 'hourly',
    amount_of_salary: 0,
    salary_history: '',
    years_of_experience: 0,
    firms: currentUser?.firms || [1],
    groups: [6], // Default to standard user group
    role: 1,
  };

  const [formData, setFormData] = useState<UserFormData>(initialFormData);
  const [passwordData, setPasswordData] = useState({
    new_password: '',
    re_new_password: '',
    current_password: '',
  });

  // Handle form submission for creating/editing users
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate password match for new users
      if (!selectedUser && formData.password !== formData.confirm_password) {
        toast({
          title: 'Error',
          description: 'Passwords do not match',
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      // Create submission data (without confirm_password)
      const submissionData = { ...formData };
      delete submissionData.confirm_password;

      if (selectedUser) {
        await api.put(`${ROUTES.API.USERS}${selectedUser.id}/`, submissionData);
        toast({
          title: 'Success',
          description: 'User updated successfully',
        });
      } else {
        await api.post(ROUTES.API.USERS, submissionData);
        toast({
          title: 'Success',
          description: 'User created successfully',
        });
      }
      setIsDialogOpen(false);
      fetchUsers();
    } catch {
      toast({
        title: 'Error',
        description: selectedUser ? 'Failed to update user' : 'Failed to create user',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (userId: number) => {
    setIsLoading(true);
    
    try {
      if (passwordData.new_password !== passwordData.re_new_password) {
        toast({
          title: 'Error',
          description: 'Passwords do not match',
          variant: 'destructive',
        });
        return;
      }

      // Different endpoint and payload based on whether it's self-change or admin-change
      if (userId === currentUser?.id) {
        // User changing their own password
        await api.post(`${ROUTES.API.USERS}change_password/`, {
          new_password: passwordData.new_password,
          re_new_password: passwordData.re_new_password,
          current_password: passwordData.current_password
        });
      } else {
        // Admin changing someone else's password
        await api.post(`${ROUTES.API.USERS}admin_change_password/`, {
          user_id: userId,  // Changed from 'id' to 'user_id' to match API expectation
          new_password: passwordData.new_password,
          re_new_password: passwordData.re_new_password
        });
      }

      toast({
        title: 'Success',
        description: 'Password changed successfully',
      });
      setIsPasswordDialogOpen(false);
      // Reset password form
      setPasswordData({
        new_password: '',
        re_new_password: '',
        current_password: '',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to change password',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle user deletion
  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`${ROUTES.API.USERS}${id}/`);
        toast({
          title: 'Success',
          description: 'User deleted successfully',
        });
        fetchUsers();
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to delete user',
          variant: 'destructive',
        });
      }
    }
  };

  // Handle user deactivation
  const handleDeactivate = async (id: number) => {
    if (confirm('Are you sure you want to deactivate this account?')) {
      try {
        await api.post(`${ROUTES.API.USERS}deactivate_account/`, { id });
        toast({
          title: 'Success',
          description: 'Account deactivated successfully',
        });
        fetchUsers();
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to deactivate account',
          variant: 'destructive',
        });
      }
    }
  };

  // Load users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
            <p className="mt-1 text-sm text-muted-foreground">People in your firm, their roles and access.</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setSelectedUser(null);
                  setFormData(initialFormData);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[625px]">
              <DialogHeader>
                <DialogTitle>
                  {selectedUser ? 'Edit User' : 'Add New User'}
                </DialogTitle>
                <DialogDescription>
                  {selectedUser ? 'Update user details' : 'Enter user details below'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="language">Language</Label>
                    <Select
                      value={formData.language}
                      onValueChange={(value) => setFormData({ ...formData, language: value })}
                    >
                      <SelectTrigger id="language">
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="nl">Dutch</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                        <SelectItem value="de">German</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="default_rate">Default Rate</Label>
                    <Input
                      id="default_rate"
                      type="number"
                      value={formData.default_rate}
                      onChange={(e) => setFormData({ ...formData, default_rate: Number(e.target.value) })}
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="yearly_target">Yearly Target (hours)</Label>
                    <Input
                      id="yearly_target"
                      type="number"
                      value={formData.yearly_target}
                      onChange={(e) => setFormData({ ...formData, yearly_target: Number(e.target.value) })}
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="form_of_salary">Salary Type</Label>
                    <Select
                      value={formData.form_of_salary}
                      onValueChange={(value) => setFormData({ ...formData, form_of_salary: value })}
                    >
                      <SelectTrigger id="form_of_salary">
                        <SelectValue placeholder="Select salary type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Hourly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="amount_of_salary">Salary Amount</Label>
                    <Input
                      id="amount_of_salary"
                      type="number"
                      value={formData.amount_of_salary}
                      onChange={(e) => setFormData({ ...formData, amount_of_salary: Number(e.target.value) })}
                    />
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="years_of_experience">Years of Experience</Label>
                    <Input
                      id="years_of_experience"
                      type="number"
                      value={formData.years_of_experience}
                      onChange={(e) => setFormData({ ...formData, years_of_experience: Number(e.target.value) })}
                    />
                  </div>
                  
                  <div className="col-span-2 space-y-2">
                    <Label>User Groups</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 1, name: "Super User" },
                        { id: 2, name: "Admin" },
                        { id: 3, name: "Accounting" },
                        { id: 4, name: "HR" },
                        { id: 5, name: "Manager" },
                        { id: 6, name: "Standard User" }
                      ].map(group => (
                        <div key={group.id} className="flex items-center space-x-2">
                          <Switch 
                            id={`group-${group.id}`}
                            checked={Boolean(formData.groups?.includes(group.id))}
                            onCheckedChange={(checked) => {
                              const groups = formData.groups || [];
                              const newGroups = checked 
                                ? [...new Set([...groups, group.id])]  // Use Set to avoid duplicates
                                : groups.filter(g => g !== group.id);
                              setFormData({ ...formData, groups: newGroups });
                            }}
                          />
                          <Label htmlFor={`group-${group.id}`}>{group.name}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? 'Processing...' : selectedUser ? 'Update User' : 'Create User'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          
          {/* Password change dialog */}
          <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Change Password</DialogTitle>
                <DialogDescription>
                  {selectedUser?.id === currentUser?.id 
                    ? 'Enter your current password and new password.' 
                    : 'Set new password for this user.'}
                </DialogDescription>
              </DialogHeader>
              <form className="space-y-4">
                {selectedUser?.id === currentUser?.id && (
                  <div className="grid gap-2">
                    <Label htmlFor="current_password">Current Password</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      required
                    />
                  </div>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="new_password">New Password</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="re_new_password">Confirm New Password</Label>
                  <Input
                    id="re_new_password"
                    type="password"
                    value={passwordData.re_new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, re_new_password: e.target.value })}
                    required
                  />
                </div>
                <DialogFooter>
                  <Button 
                    type="button" 
                    onClick={() => selectedUser && handlePasswordChange(selectedUser.id)}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : 'Change Password'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4 mb-4">
              <SearchInput
                placeholder="Search users..."
                value={searchQuery}
                onChange={setSearchQuery}
                className="flex-1"
              />
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden md:table-cell">Email</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Groups</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    users
                      .filter((user) => {
                        const searchTerm = searchQuery.toLowerCase();
                        return (
                          user.first_name?.toLowerCase().includes(searchTerm) ||
                          user.last_name?.toLowerCase().includes(searchTerm) ||
                          user.email?.toLowerCase().includes(searchTerm) ||
                          user.title?.toLowerCase().includes(searchTerm)
                        );
                      })
                      .sort((a, b) => {
                        // First, sort by active status (active users first)
                        if (a.is_active !== false && b.is_active === false) return -1;
                        if (a.is_active === false && b.is_active !== false) return 1;
                        
                        // Then sort alphabetically by first name, then last name
                        const firstNameCompare = (a.first_name || '').localeCompare(b.first_name || '');
                        if (firstNameCompare !== 0) return firstNameCompare;
                        return (a.last_name || '').localeCompare(b.last_name || '');
                      })
                      .map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            {user.first_name} {user.last_name}
                            {user.is_active === false && (
                              <span className="ml-2 inline-block bg-red-100 text-red-800 rounded-full px-2 py-0.5 text-xs font-medium">
                                Deactivated
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="hidden md:table-cell">{user.email}</TableCell>
                          <TableCell>{user.title}</TableCell>
                          <TableCell>
                            {(user.groups || []).map(group => {
                              let groupName = '';
                              switch(group) {
                                case 1: groupName = 'Super User'; break;
                                case 2: groupName = 'Admin'; break;
                                case 3: groupName = 'Accounting'; break;
                                case 4: groupName = 'HR'; break;
                                case 5: groupName = 'Manager'; break;
                                case 6: groupName = 'User'; break;
                                default: groupName = `Group ${group}`;
                              }
                              return <span key={group} className="inline-block rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground mr-1 mb-1">{groupName}</span>;
                            })}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-0.5">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setFormData({
                                    ...user,
                                  });
                                  setIsDialogOpen(true);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                                <span className="sr-only">Edit</span>
                              </Button>
                              
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setPasswordData({
                                    new_password: '',
                                    re_new_password: '',
                                    current_password: '',
                                  });
                                  setIsPasswordDialogOpen(true);
                                }}
                              >
                                <Lock className="h-4 w-4" />
                                <span className="sr-only">Change Password</span>
                              </Button>
                              
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                onClick={() => handleDeactivate(user.id)}
                                disabled={user.id === currentUser?.id} // Can't deactivate yourself
                              >
                                <UserX className="h-4 w-4" />
                                <span className="sr-only">Deactivate</span>
                              </Button>
                              
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => handleDelete(user.id)}
                                disabled={user.id === currentUser?.id} // Can't delete yourself
                              >
                                <Trash2 className="h-4 w-4" />
                                <span className="sr-only">Delete</span>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}